import { useCallback, useMemo } from "react";

import type { ObservationsMapWidgetConfiguration } from "@/lib/api";
import { ObservationsMapBaseLayerStyle } from "@/lib/mapbox/base-layer-styles";
import { useAdminDashboard } from "@/features/admin/hooks";
import { useDashboardLocationsQuery } from "@/features/admin/features/dashboards/hooks/queries";
import { ObservationsMapView } from "@/features/shared/widgets/observations-map/observations-map";
import { buildObservationsMapLayerConfigs } from "@/features/shared/widgets/observations-map/build-observations-map-layer-configs";
import { useObservationsMapResolvedConfig } from "@/features/shared/widgets/observations-map/use-observations-map-resolved-config";
import { makeTimeSeriesConfigs } from "@/features/shared/widgets/map/time-series/make-time-series-configs";
import { toTimeSeriesParameterOption } from "@/features/shared/widgets/map/time-series/time-series-parameter-option.utils";
import { useSelectedTimeRange } from "@/features/dashboard/store";
import { WidgetContextHost } from "@/features/shared/widget-context/widget-context-host";
import { WidgetContextToolbar } from "@/features/shared/widget-context/widget-context-toolbar";
import { TimeRangeControl } from "@/features/shared/widget-context/controls/time-range-control";
import { RefreshControl } from "@/features/shared/widget-context/controls/refresh-control";
import { initialViewportPresets } from "@/features/widget-forms/shared/initial-viewport-presets";
import previewStyles from "@/features/widget-forms/forms/map-widget-editor/widget-preview.module.css";

type ObservationsMapPreviewProps = {
  /** Live draft config from the editor form (may be partial while editing). */
  configuration: Partial<ObservationsMapWidgetConfiguration>;
};

/**
 * Fill a partial draft with the defaults the renderer needs so an in-progress
 * configuration still previews (empty layer/series arrays, default base layer
 * and viewport). Mirrors the seeding the editor form itself applies.
 */
function toPreviewConfiguration(
  draft: Partial<ObservationsMapWidgetConfiguration>,
): ObservationsMapWidgetConfiguration {
  return {
    ...draft,
    type: "observations_map",
    parameter_map_layer_configurations: draft.parameter_map_layer_configurations ?? [],
    static_map_overlay_configurations: draft.static_map_overlay_configurations ?? [],
    parameter_time_series_configurations: draft.parameter_time_series_configurations ?? [],
    parameter_display_groups: draft.parameter_display_groups ?? [],
    base_layer_style: draft.base_layer_style || ObservationsMapBaseLayerStyle.Monochrome,
    initial_viewport_bounds: draft.initial_viewport_bounds ?? initialViewportPresets.nl,
    show_all_locations: draft.show_all_locations ?? true,
    weather_stations_enabled_by_default: draft.weather_stations_enabled_by_default ?? true,
  } as ObservationsMapWidgetConfiguration;
}

/**
 * CMS "smart-equivalent" of the dashboard {@link ObservationsMap}: drives the
 * dumb {@link ObservationsMapView} from the editor's live draft configuration
 * rather than a saved/published widget. It resolves the config against the
 * observed catalogs, builds the same layer set (filtering/prioritizing stations
 * by the ACTIVE dashboard locations, since the draft is not yet published), and
 * seeds the time dimension from the dashboard being edited via
 * {@link WidgetContextHost}.
 *
 * Changes to the layer set or time-series config remount the map (the map state
 * machine only reads its layer input at init), so committed edits reflect live;
 * base-layer-style edits update in place. Firing real tile/series/location
 * requests is intentional (same as the dashboard).
 */
export function ObservationsMapPreview({ configuration }: ObservationsMapPreviewProps) {
  const dashboard = useAdminDashboard(false);

  return (
    <WidgetContextHost dashboardId={dashboard?.id}>
      <ObservationsMapPreviewContent configuration={configuration} />
    </WidgetContextHost>
  );
}

/**
 * Controls for the obs-map preview title bar: the shared time-range picker plus a
 * manual refresh. Must be rendered inside the preview's {@link WidgetContextHost}
 * (as a sibling of {@link ObservationsMapPreviewContent}) so it drives the same
 * runtime + refresh contexts the map reads.
 */
export function ObservationsMapPreviewToolbar() {
  return (
    <WidgetContextToolbar>
      <TimeRangeControl />
      <RefreshControl />
    </WidgetContextToolbar>
  );
}

/**
 * Inner view rendered inside the {@link WidgetContextHost}: reads the scoped time
 * range and timezone from the runtime context (so a toolbar / seeded default
 * drives the map).
 */
export function ObservationsMapPreviewContent({
  configuration,
}: ObservationsMapPreviewProps) {
  const timeRange = useSelectedTimeRange();
  const dashboard = useAdminDashboard(false);
  // The CMS previews an unpublished draft, so mirror the dashboard's station
  // filtering using the ACTIVE (admin) dashboard locations rather than published
  // ones. This is the same source the location-filter panel counts, so the
  // preview shows exactly the configured stations when "show all" is off.
  const dashboardLocationsQuery = useDashboardLocationsQuery(dashboard?.id ?? "");

  const previewConfiguration = useMemo(
    () => toPreviewConfiguration(configuration),
    [
      configuration.parameter_map_layer_configurations,
      configuration.static_map_overlay_configurations,
      configuration.parameter_time_series_configurations,
      configuration.parameter_display_groups,
      configuration.base_layer_style,
      configuration.initial_viewport_bounds,
      configuration.show_all_locations,
      configuration.weather_stations_enabled_by_default,
    ],
  );

  const {
    configuredLayers,
    configuredParameters,
    timeSeriesConfigurations,
    displayGroups,
    hasStationLayer,
    layersPending,
  } = useObservationsMapResolvedConfig(previewConfiguration);

  const showAllLocations = previewConfiguration.show_all_locations ?? true;
  const weatherStationsEnabledByDefault =
    previewConfiguration.weather_stations_enabled_by_default ?? true;

  // When "show all" is on, the full station set renders immediately; when off,
  // wait for the active dashboard locations so the layer filters to exactly the
  // configured stations instead of transiently rendering none.
  const stationLayerReady = showAllLocations || !dashboardLocationsQuery.isPending;

  const catalogLabel = useCallback(
    (parameterId: string) => configuredParameters.find((p) => p.id === parameterId)?.label,
    [configuredParameters],
  );

  const mapLayerConfigs = useMemo(
    () =>
      buildObservationsMapLayerConfigs({
        configuredLayers,
        hasStationLayer,
        stationLayerReady,
        displayGroups,
        timeSeriesConfigurations,
        resolveParameterLabel: catalogLabel,
        showAllLocations,
        weatherStationsEnabledByDefault,
        dashboardLocations: dashboardLocationsQuery.data,
      }),
    [
      configuredLayers,
      hasStationLayer,
      stationLayerReady,
      displayGroups,
      timeSeriesConfigurations,
      catalogLabel,
      showAllLocations,
      weatherStationsEnabledByDefault,
      dashboardLocationsQuery.data,
    ],
  );

  const timeSeriesConfigs = useMemo(() => {
    const parameterOptions = configuredParameters.map((parameter) =>
      toTimeSeriesParameterOption(parameter),
    );
    return makeTimeSeriesConfigs(timeSeriesConfigurations, parameterOptions);
  }, [timeSeriesConfigurations, configuredParameters]);

  const mapLayerInput = useMemo(
    () => ({ layerConfigs: mapLayerConfigs, timeRange }),
    [mapLayerConfigs, timeRange],
  );

  // Ordered dashboard parameter-location ids; part of the actor key so the map
  // re-initializes once the active locations arrive (or their order changes),
  // in both "show all" and filtered modes.
  const dashboardLocationOrderKey = useMemo(
    () =>
      (dashboardLocationsQuery.data ?? [])
        .map((location) => location.parameter_location_id?.trim())
        .filter((id): id is string => !!id)
        .join(","),
    [dashboardLocationsQuery.data],
  );

  // The map state machine only consumes its layer input at init, so remount it
  // whenever the layer set or the config that shapes the layers changes.
  const actorKey = useMemo(
    () =>
      JSON.stringify({
        layerIds: mapLayerConfigs.map((config) => config.layer.id),
        showAllLocations,
        weatherStationsEnabledByDefault,
        timeSeriesConfigurations,
        displayGroups,
        dashboardLocationOrderKey,
        stationLayerReady,
      }),
    [
      mapLayerConfigs,
      showAllLocations,
      weatherStationsEnabledByDefault,
      timeSeriesConfigurations,
      displayGroups,
      dashboardLocationOrderKey,
      stationLayerReady,
    ],
  );

  return (
    <div className={previewStyles.host}>
      <ObservationsMapView
        key={actorKey}
        configuration={previewConfiguration}
        mapLayerInput={mapLayerInput}
        timeSeriesConfigs={timeSeriesConfigs}
        timeSeriesConfigurations={timeSeriesConfigurations}
        configuredParameters={configuredParameters}
        hasStationLayer={hasStationLayer}
        isLoadingLayers={layersPending}
        containerHeight="100cqh"
      />
    </div>
  );
}
